import React from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Typography,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ProgressPanel from './ProgressPanel';

const ChartPanel = ({
  t,
  chartDataWithModels,
  tooltipFormatter,
  showSolowSmith,
  showLogistic,
  showGompertz,
  onToggleSolowSmith,
  onToggleLogistic,
  onToggleGompertz,
  solowSmithAvailable,
  logisticAvailable,
  gompertzAvailable,
  solowSmithBandAvailable,
  logisticBandAvailable,
  gompertzBandAvailable,
  solowCurveBandAvailable,
  logisticCurveBandAvailable,
  gompertzCurveBandAvailable,
  fitLoading,
  fitStage,
  fitProgress,
  // optional ref to allow parent export
  chartRef,
}) => (
  <Paper elevation={3} sx={{ p: { xs: 1, sm: 2, md: 3 }, mb: 3, backdropFilter: 'blur(5px)', backgroundColor: 'rgba(30,30,30,0.5)' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <TimelineIcon sx={{ mr: 1 }} />
      <Typography variant="h6" sx={{ flexGrow: 1 }}>{t('estimationPage.chartTitle')}</Typography>
      <FormGroup row>
        <FormControlLabel
          control={(
            <Checkbox
              checked={showSolowSmith}
              onChange={(event) => onToggleSolowSmith(event.target.checked)}
              disabled={!solowSmithAvailable}
            />
          )}
          label={t('estimationPage.showSolowSmith')}
        />
        <FormControlLabel
          control={(
            <Checkbox
              checked={showLogistic}
              onChange={(event) => onToggleLogistic(event.target.checked)}
              disabled={!logisticAvailable}
            />
          )}
          label={t('estimationPage.showLogistic')}
        />
        <FormControlLabel
          control={(
            <Checkbox
              checked={showGompertz}
              onChange={(event) => onToggleGompertz(event.target.checked)}
              disabled={!gompertzAvailable}
            />
          )}
          label={t('estimationPage.showGompertz')}
        />
      </FormGroup>
    </Box>

    {chartDataWithModels.length > 0 ? (
      <Box sx={{ position: 'relative' }}>
        <div ref={chartRef}>
          <ResponsiveContainer width="100%" height={420}>
            <ComposedChart data={chartDataWithModels} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis label={{ value: t('cumulativeCount'), angle: -90, position: 'insideLeft' }} />
              <Tooltip
                formatter={tooltipFormatter}
                contentStyle={{ backgroundColor: 'rgba(30, 30, 30, 0.95)', border: '1px solid rgba(127, 255, 212, 0.3)', borderRadius: '8px' }}
              />
              <Legend />
              <Line type="monotone" dataKey="observed" stroke="#82ca9d" strokeWidth={2} dot={false} name={t('estimationPage.observedLabel')} />
              {showSolowSmith && solowSmithAvailable && solowSmithBandAvailable && (
                <Area
                  type="monotone"
                  dataKey="solowSmithRange"
                  stroke="none"
                  fill="#7ad6e8"
                  fillOpacity={0.3}
                  name={solowCurveBandAvailable
                    ? t('estimationPage.bandSolowSmithCurve')
                    : t('estimationPage.bandSolowSmithTotal')}
                />
              )}
              {showLogistic && logisticAvailable && logisticBandAvailable && (
                <Area
                  type="monotone"
                  dataKey="logisticRange"
                  stroke="none"
                  fill="#7f7be6"
                  fillOpacity={0.35}
                  name={logisticCurveBandAvailable
                    ? t('estimationPage.bandLogisticCurve')
                    : t('estimationPage.bandLogisticTotal')}
                />
              )}
              {showGompertz && gompertzAvailable && gompertzBandAvailable && (
                <Area
                  type="monotone"
                  dataKey="gompertzRange"
                  stroke="none"
                  fill="#ffd16a"
                  fillOpacity={0.35}
                  name={gompertzCurveBandAvailable
                    ? t('estimationPage.bandGompertzCurve')
                    : t('estimationPage.bandGompertzTotal')}
                />
              )}
              {showSolowSmith && solowSmithAvailable && (
                <>
                  <Line type="monotone" dataKey="solowSmith" stroke="#4dd0e1" strokeWidth={2} dot={false} name={t('estimationPage.modelSolowSmith')} />
                </>
              )}
              {showLogistic && logisticAvailable && (
                <>
                  <Line type="monotone" dataKey="logistic" stroke="#8884d8" strokeWidth={2} dot={false} name={t('estimationPage.modelLogistic')} />
                </>
              )}
              {showGompertz && gompertzAvailable && (
                <>
                  <Line type="monotone" dataKey="gompertz" stroke="#ffc658" strokeWidth={2} dot={false} name={t('estimationPage.modelGompertz')} />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {fitLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.45)',
              backdropFilter: 'blur(2px)',
              zIndex: 1,
            }}
          >
            <ProgressPanel
              size={28}
              title={t('estimationPage.analysisInProgress')}
              stage={fitStage}
              progress={fitProgress}
              containerSx={{ width: '100%', maxWidth: 360, px: 2 }}
            />
          </Box>
        )}
      </Box>
    ) : (
      <Paper elevation={0} sx={{ p: 4, textAlign: 'center', backgroundColor: 'transparent' }}>
        <Typography color="text.secondary">{t('noData')}</Typography>
      </Paper>
    )}
  </Paper>
);

export default ChartPanel;
